import type { BasicBookingFragment } from '@drycut/graphql';

import { formatDateRelativeInWeek, type FormattedDurationOptions } from '@drycut/ui-format-utils';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import { WaitingTime } from './WaitingTime';

type Props = {
  booking?: BasicBookingFragment;
} & FormattedDurationOptions;

export function BookingWaitingTime({ booking, ...options }: Props): React.ReactElement {
  const [time, setTime] = useState<number>(booking?.waitingTime || 0);
  const [shouldStop, setShouldStop] = useState<boolean>(false);

  // Get the estimate start time based on the booking waiting time. The rounding trick is to ensure
  // that we always round up to the next minute so if the store opens at 10:00 we don't show 9:59
  // which can happen, depending on DateTime.now() seconds value.
  const estimateStartTime = useMemo(
    () =>
      DateTime.now()
        .plus({ seconds: Number(booking?.waitingTime) + 59 }) // trick to always round up
        .startOf('minute'), // to the next minute
    [booking?.waitingTime],
  );

  const formattedRelativeTime = useMemo(() => {
    return formatDateRelativeInWeek({ date: estimateStartTime, options: { steps: options.steps } });
  }, [estimateStartTime, options.steps]);

  // TODO: move to a hook like we do on StoreWaitingTime?
  useInterval(
    () => {
      if (time <= 0) {
        setShouldStop(true);
      } else {
        setTime((prevTime) => prevTime - 1);
      }
    },
    shouldStop ? null : 1000,
  );

  // If we get an update from the server, we stop the interval and update the time. This will happen
  // only when the component is under a RealTimeStoreProvider.
  useEffect(() => {
    const updatedTime = booking?.waitingTime;

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (typeof updatedTime === 'number') {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state, react-hooks/set-state-in-effect
      setTime(updatedTime);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setShouldStop(false);
    }
  }, [booking]);

  return <WaitingTime time={time} relativeTime={formattedRelativeTime} options={options} />;
}
